import { useEffect, useRef, useState } from "react";
import { ASSISTANT_CONFIG, isEligiblePath, pageTypeOf } from "./config";
import { triggerAssistantEvent } from "./analytics";

const TRIGGERED_KEY = "isa_triggered";
const FORM_SUBMITTED_KEY = "isa_form_submitted";

function sessionValue(key: string): string | null {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}

function setSessionValue(key: string, value: string): void {
  try { window.sessionStorage.setItem(key, value); } catch { /* Storage can be blocked; the current route remains safe. */ }
}

function isExternalFormField(element: Element | null): boolean {
  if (!(element instanceof HTMLElement)) return false;
  if (!(["INPUT", "TEXTAREA", "SELECT"] as string[]).includes(element.tagName)) return false;
  return !element.closest("[data-sales-assistant]");
}

export function shouldFireAssistantTrigger(input: {
  activeMs: number;
  visible: boolean;
  automaticViewport: boolean;
  formFocused: boolean;
  graceUntil: number;
  now: number;
}): boolean {
  return input.activeMs >= ASSISTANT_CONFIG.triggerSeconds * 1000 &&
    input.visible &&
    input.automaticViewport &&
    !(ASSISTANT_CONFIG.deferWhileFormFieldFocused && (input.formFocused || input.now < input.graceUntil));
}

/** Automatic prompts are intentionally limited to horizontal desktop-class screens. */
export function isAutomaticAssistantViewport(width: number, height: number): boolean {
  return width >= ASSISTANT_CONFIG.automaticMinimumViewportWidth && width > height;
}

/** Counts only visible, active time and never steals focus when it fires. */
export function useActiveTimeTrigger(path: string): boolean {
  const [fired, setFired] = useState(false);
  const [automaticViewport, setAutomaticViewport] = useState(false);
  const activeMs = useRef(0);
  const last = useRef<number | null>(null);

  useEffect(() => {
    const updateViewport = () => setAutomaticViewport(isAutomaticAssistantViewport(window.innerWidth, window.innerHeight));
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    activeMs.current = 0;
    last.current = null;
    setFired(false);
    if (!ASSISTANT_CONFIG.enabled || !isEligiblePath(path) || !automaticViewport) return;
    if (ASSISTANT_CONFIG.oncePerSession && sessionValue(TRIGGERED_KEY) === "1") return;
    if (sessionValue(FORM_SUBMITTED_KEY) === "1") return;

    let graceUntil = 0;
    const onFocusOut = (event: FocusEvent) => {
      if (isExternalFormField(event.target as Element)) graceUntil = Date.now() + ASSISTANT_CONFIG.formBlurGraceSeconds * 1000;
    };
    const onSubmit = (event: Event) => {
      const form = event.target as HTMLFormElement | null;
      if (form && !form.closest("[data-sales-assistant]")) setSessionValue(FORM_SUBMITTED_KEY, "1");
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") last.current = null;
    };
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("submit", onSubmit, true);
    document.addEventListener("visibilitychange", onVisibility);

    const timer = window.setInterval(() => {
      const now = Date.now();
      const visible = document.visibilityState === "visible";
      if (!ASSISTANT_CONFIG.pauseWhenHidden || visible) {
        if (last.current !== null) activeMs.current += now - last.current;
        last.current = now;
      } else {
        last.current = null;
      }
      if (sessionValue(FORM_SUBMITTED_KEY) === "1") {
        window.clearInterval(timer);
        return;
      }
      const formFocused = isExternalFormField(document.activeElement);
      if (shouldFireAssistantTrigger({ activeMs: activeMs.current, visible, automaticViewport, formFocused, graceUntil, now })) {
        setSessionValue(TRIGGERED_KEY, "1");
        triggerAssistantEvent(pageTypeOf(path), path);
        setFired(true);
        window.clearInterval(timer);
      }
    }, 250);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("submit", onSubmit, true);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [path, automaticViewport]);

  return fired;
}

export function hasAssistantEngagement(): boolean {
  return sessionValue("isa_engaged") === "1";
}

export function markAssistantEngaged(): void {
  setSessionValue("isa_engaged", "1");
}
